import React from 'react'
import { Link as ReactRouterLink } from 'react-router'
import {
  defaultTheme,
  majorScale,
  Heading,
  Menu,
  Pane,
  Paragraph,
  Popover,
  Spinner,
  Text,
  Badge,
  Tooltip,
  Icon,
  Link,
  Position
} from 'evergreen-ui'
import { AvatarDropdown } from 'evergreen-draft'
import { logoutPath, settingsNotificationsPath } from 'lib/path-helpers'
import { changeWorkspaceClicked } from 'analytics/global-navigation'
import { workspacesPath } from 'lib/path-helpers/workspaces'
import { userSettingsPath } from 'lib/path-helpers/user'
import { User } from '../../context/UserContext'

interface UserDropdownProps {
  isContactSupportVisible: boolean
  isPartnerPortalVisible: boolean
  user: User
  isDeveloperCenterVisible?: boolean
  isErrored?: boolean
  isLoading?: boolean
  isWorkspaceOwner?: boolean
  showChangeWorkspace?: boolean
  workspaceName?: string
  workspacePlan?: string
  workspaceSlug?: string
}

class UserDropdown extends React.Component<UserDropdownProps> {
  static defaultProps = {
    showChangeWorkspace: false,
    workspaceName: '',
    isDeveloperCenterVisible: false
  }

  onClickChangeWorkspace = () => {
    changeWorkspaceClicked({ label: 'Account settings' })
  }

  renderErrorState() {
    return (
      <Paragraph size={300} width={280} padding={majorScale(1)}>
        An error has occurred while loading data for this dropdown. Please reload the page.
      </Paragraph>
    )
  }

  renderLoadingState() {
    return (
      <Pane display="flex" justifyContent="center" paddingY={majorScale(4)}>
        <Spinner size={20} />
      </Pane>
    )
  }

  renderUserMenu(closePopoverFn: () => void) {
    const {
      isContactSupportVisible,
      isPartnerPortalVisible,
      showChangeWorkspace,
      user,
      workspacePlan,
      workspaceName,
      workspaceSlug,
      isDeveloperCenterVisible,
      isWorkspaceOwner
    } = this.props

    return (
      <Pane>
        <Pane padding={majorScale(2)} maxWidth={majorScale(30)} backgroundColor={defaultTheme.scales.neutral.N1A}>
          <Heading is="h3">{workspaceName || user.name}</Heading>
          {workspacePlan && (
            <Text color="muted" size={300}>
              {workspacePlan} Plan
            </Text>
          )}
          <Pane>
            <Text>{user.email}</Text>
          </Pane>
          {workspacePlan && (
            <Pane display="flex" alignItems="center" paddingTop={majorScale(1)}>
              <Text color="muted" size={300} paddingRight={majorScale(1)}>
                {isWorkspaceOwner ? 'Workspace Owner' : 'Workspace Member'}
              </Text>
              <Tooltip
                statelessProps={{
                  maxWidth: isWorkspaceOwner ? 260 : 300
                }}
                position={Position.BOTTOM_LEFT}
                content={
                  isWorkspaceOwner ? (
                    <Paragraph color="white">
                      Owners manage all aspects of the workspace.{' '}
                      <Link href="https://segment.com/docs/iam/" target="_blank" color="green">
                        Learn more
                      </Link>
                    </Paragraph>
                  ) : (
                    <Paragraph color="white">
                      Members have access to specific
                      <Paragraph color="white">
                        products and resource types.{' '}
                        <Link href="https://segment.com/docs/iam/" target="_blank" color="green">
                          Learn more
                        </Link>
                      </Paragraph>
                    </Paragraph>
                  )
                }
              >
                <Icon icon="info-sign" />
              </Tooltip>
            </Pane>
          )}
        </Pane>

        <Menu.Divider />

        <Menu.Group>
          <Menu.Item is={ReactRouterLink} to={userSettingsPath()}>
            User Settings
          </Menu.Item>
          {workspaceSlug && (
            <Menu.Item
              is={ReactRouterLink}
              to={settingsNotificationsPath({ workspaceSlug })}
              onSelect={() => closePopoverFn()}
            >
              Notification Settings
            </Menu.Item>
          )}
        </Menu.Group>

        <Menu.Divider />

        <Menu.Group>
          <Menu.Item is={ReactRouterLink} to="https://segment.com/docs" rel="external" target="_blank">
            Documentation
          </Menu.Item>
          {isContactSupportVisible && (
            <Menu.Item is={ReactRouterLink} href="https://segment.com/help/contact" rel="external" target="_blank">
              Contact Support
            </Menu.Item>
          )}
        </Menu.Group>

        <Menu.Divider />

        <Menu.Group>
          {isDeveloperCenterVisible && (
            <Menu.Item is="a" href="/developer/apps">
              <Pane display="flex" alignItems="center" justifyContent="space-between">
                <Text>Developer Center</Text>
                <Badge color="neutral">Beta</Badge>
              </Pane>
            </Menu.Item>
          )}
          {isPartnerPortalVisible && (
            <Menu.Item is={ReactRouterLink} to="/partner-portal/integration">
              Partner Portal (Internal Use)
            </Menu.Item>
          )}
          {showChangeWorkspace && (
            <Menu.Item is={ReactRouterLink} to={workspacesPath()} onSelect={this.onClickChangeWorkspace}>
              <Text color={defaultTheme.palette.green.base}>Change Workspace</Text>
            </Menu.Item>
          )}
          <Menu.Item is={ReactRouterLink} href={logoutPath()} rel="external">
            Logout
          </Menu.Item>
        </Menu.Group>
      </Pane>
    )
  }

  render() {
    const { isLoading, isErrored, user } = this.props

    let content
    if (isLoading) {
      content = this.renderLoadingState()
    } else if (isErrored) {
      content = this.renderErrorState()
    } else {
      content = ({ close }) => this.renderUserMenu(close)
    }

    return (
      <Popover bringFocusInside={false} content={content}>
        <AvatarDropdown avatarProps={{ name: user.name }} />
      </Popover>
    )
  }
}

export default UserDropdown
